import React, { useState } from "react";
import client, { signUpload } from "@culturehq/client";
import { Form, ImageField, ModalDialog, NumberField, SelectField, StringField, SubmitButton } from "@culturehq/components";

import { useDoor, useTryCatch } from "app/hooks";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

const APPROVAL_OPTIONS = [
  {
    label: t("Open - anyone can use this recognition type."),
    value: "no_approval_needed"
  }, {
    label: t("Admin approval - admins must approve recognitions of this type that are created before they are published."),
    value: "approval_needed"
  }, {
    label: t("Admin only - only admins may use this recognition type."),
    value: "admin_only"
  }
];

const getInitialValues = ({ name, approval, image, points }) => ({
  name,
  approval: approval || APPROVAL_OPTIONS[0].value,
  image: image && image.originalUrl,
  points: points || 50
});

const pointsValidator = points => {
  if (!points) {
    return t("Required");
  }

  if (points < 50) {
    return t("Score must be at least 50.");
  }

  return null;
};

const RecognitionTypeModal = ({ children, recognitionType, onCreate, onUpdate }) => {
  const { open, onOpen, onClose } = useDoor(false);
  const [imageProgress, setImageProgress] = useState(null);

  const [{ error }, onSubmit] = useTryCatch(
    async ({ image, ...others }) => {
      const values = others;
      if (image && image.size) {
        values.imageUrl = await signUpload(image, setImageProgress);
      }

      if (recognitionType) {
        const response = await client.updateRecognitionType({
          recognitionTypeId: recognitionType.id, ...values
        });

        onUpdate(response.recognitionType);
      } else {
        const response = await client.createRecognitionType(values);
        onCreate(response.recognitionType);
      }

      onClose();
    },
    [recognitionType, onClose, onCreate, onUpdate]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onClose}>
          <ModalDialog.Heading onClose={onClose}>
            {
              recognitionType
                ? <Trans>Edit Type</Trans>
                : <Trans>Add a Type</Trans>
            }
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form onSubmit={onSubmit} initialValues={getInitialValues(recognitionType || {})}>
              <StringField name="name" autoFocus required>
                <Trans>Name</Trans>
              </StringField>
              <SelectField name="approval" required options={APPROVAL_OPTIONS}>
                <Trans>Privileges</Trans>
              </SelectField>
              <ImageField name="image" progress={imageProgress} required>
                <Trans>Image</Trans>
              </ImageField>
              <NumberField name="points" validator={pointsValidator}>
                <Trans>Score</Trans>
              </NumberField>
              <SubmitButton primary>
                {submitting => (
                  submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>
                )}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default RecognitionTypeModal;
